import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/hooks/useAuth';
import { requiredDocumentsByRole } from './reporterUtils';
import { RoleEnum, UserDTO } from 'src/api';
import _ from 'lodash';
import { UserDocument } from 'src/api/models/UserDTO';

type ReporterDocument = UserDocument & {
  type: 'file' | 'text';
  code: string;
  label: string;
  file?: File | null;
  key?: string;
  templateDocument?: {
    bucket: string;
    filename: string;
  };
};

export function useReporterDocuments(role?: RoleEnum | null, userDocuments: UserDTO['documents'] = []) {
  let { userRole: loggedUserRole } = useAuth();
  const [documents, setDocuments] = useState<ReporterDocument[]>([]);

  const matchExistingDocuments = (docsByCode: any, userDocs?: UserDTO['documents']) => {
    if (!userDocs) return docsByCode;

    for (const existingDoc of userDocs) {
      if (!existingDoc?.fileType || !existingDoc?.key) {
        continue;
      }

      if (docsByCode[existingDoc.fileType]) {
        docsByCode[existingDoc.fileType].key = existingDoc.key;
        docsByCode[existingDoc.fileType].fileName = existingDoc.fileName;
      }
    }

    return docsByCode;
  };

  const initDocuments = useCallback((newUserRole?: RoleEnum | null, userDocs?: UserDTO['documents']) => {
    if (newUserRole != RoleEnum.SEGNALATORE) {
      setDocuments([]);
      return;
    }

    let docsByCode = _.clone(requiredDocumentsByRole(loggedUserRole));

    if (!userDocs?.length) {
      return setDocuments(Object.values(docsByCode));
    }

    if (_.isEmpty(docsByCode)) {
      return setDocuments([]);
    }

    docsByCode = matchExistingDocuments(docsByCode, userDocs);

    setDocuments(Object.values(docsByCode));
  }, [loggedUserRole]);


  const handleDocument = (code?: string | null, file?: File | null) => {
    if (!code) return;

    setDocuments((docs) => {
      return docs.map(doc => {
        if (doc.code === code) {
          doc.fileType = code;
          file?.name && (doc.fileName = file?.name);
          doc.file = file;
          // @ts-ignore
          doc.key = undefined;
        }
        return doc;
      })
    })
  }

  useEffect(() => {
    initDocuments(role, userDocuments);
  }, [role, userDocuments?.length])

  const reporterDocuments = useMemo(() => {
    return [...documents].map(item => ({ ...item, isUploaded: Boolean(item.key) || !!item.file || false }));
  }, [documents])

  return {
    reporterDocuments,
    initDocuments,
    handleReporterDocument: handleDocument,
  };
}
